import React from "react";
import { Card, CardContent, CardActions } from "Components/Base/Surfaces/Card";
import { Box } from "Components/Base/Layout/Box";
import { Typography } from "Components/Base/DataDisplay/Typography";
import ItemIcon from "./ItemIcon";
import { CardNoImage } from "./functions";
import {
  CARD_IMAGE_SETTINGS,
  CARD_NO_IMAGE_SETTINGS,
} from "./constants";
import { STYLE_CARD_HOVER } from "./style";
import zIndex from "@mui/material/styles/zIndex";

const CardHeader = ({ displayIcon, type, chips }) => (
  <>
    {displayIcon && (
      <Box position="absolute" top={10} left={16}>
        <ItemIcon type={type} />
      </Box>
    )}

    <Box
      position="absolute"
      top={10}
      right={16}
      sx={{
        display: "flex",
        flexDirection: "column",
        zIndex: 1,
      }}
    >
      {chips}
    </Box>
  </>
);

const CardBody = ({ title, content, variant = "h3" }) => (
  <CardContent>
    <Typography
      className="elipssis"
      variant={variant}
      title={title}
      id="learningActivityCardTitle"
    >
      {title}
    </Typography>
    {content}
  </CardContent>
);

const CardFooter = ({ actions }) => (
  <CardActions>
    <Box position="absolute" bottom={10} right={16} sx={{ display: "flex" }}>
      {actions}
    </Box>
  </CardActions>
);

/**
 * A card component for displaying learning activities like courses, programs etc.
 * @component
 * @param {string} props.id - Unique identifier for the card
 * @param {string} props.title - Title text to display in the card
 * @param {boolean} [props.displayImage=false] - Whether to show an image or placeholder
 * @param {boolean} [props.displayIcon=true] - Whether to show an icon or not
 * @param {string} props.type - Type of learning activity (online, classroom etc)
 * @param {React.ReactNode} props.chips - Status chips/tags to display (completed, mandatory etc)
 * @param {React.ReactNode} props.content - Main content to display in card body
 * @param {React.ReactNode} props.actions - Action buttons/links to display at bottom
 * @param {React.ReactNode} props.imageComponent - Image component to display when displayImage is true
 * @param {React.ComponentType} [props.headerComponent] - Custom header component that overrides default header
 * @param {React.ComponentType} [props.bodyComponent] - Custom body component that overrides default body
 * @param {React.ComponentType} [props.footerComponent] - Custom footer component that overrides default footer
 * @param {React.ComponentType} [props.imageRenderer] - Custom image renderer that overrides default image handling
 * @param {string} [props.variant = "h3"] - Variant of the title to display in the card body
 * @returns {React.ReactElement} A card component displaying learning activity information with customizable layout
 * @example
 * <LearningActivityCard
 *   id="course-123"
 *   title="Introduction to React"
 *   type="online"
 *   chips={<StatusChip status="completed" />}
 *   content={<CourseDescription />}
 *   actions={<EnrollButton />}
 * />
 */
const LearningActivityCard = ({
  id,
  title,
  displayImage = false,
  displayIcon = false,
  type,
  chips,
  content,
  actions,
  imageComponent,
  // Allow customization of the component through props (OCP)
  headerComponent: CustomHeader,
  bodyComponent: CustomBody,
  footerComponent: CustomFooter,
  imageRenderer: CustomImageRenderer,
  variant,
}) => {
  // Use custom components if provided, otherwise use default components
  const Header =
    CustomHeader ||
    (() => <CardHeader displayIcon={displayIcon} type={type} chips={chips} />);

  const Body =
    CustomBody || (() => <CardBody title={title} content={content} variant={variant} />);

  const Footer = CustomFooter || (() => <CardFooter actions={actions} />);

  const ImageRenderer =
    CustomImageRenderer ||
    (() => (displayImage ? imageComponent : <CardNoImage />));

  return (
    <Card
      id={`learningActivityCard${id}`}
      className="learningActivityCard relative"
      variant="outlined"
      sx={{
        ...(displayImage ? CARD_IMAGE_SETTINGS : CARD_NO_IMAGE_SETTINGS),
        ...STYLE_CARD_HOVER,
      }}
    >
      <Header />
      <ImageRenderer />
      <Body />
      <Footer />
    </Card>
  );
};

export default LearningActivityCard;
